<template>
  <div v-if="showBio" class="sp-finder-result-container">
    <article class="sp-finder-result --layout-bio" :class="bioModifiers" part="base">
      <FinderResultThumbnail class="sp-finder-result__thumbnail" layout="bio" v-bind="bioThumbnailProps">
        <template #overlay>
          <slot name="thumbnail-overlay" :display-name="displayName"></slot>
        </template>
        <template #details>
          <FinderResultRatings v-if="showRatings" :rating="result.rating" :show-count="showRatingCount" />
          <slot name="thumbnail-details"></slot>
        </template>
      </FinderResultThumbnail>

      <FinderResultHeading
        class="sp-finder-result__heading"
        title-type="custom"
        :name="displayName"
        :url="result.url"
        :provider-type="result.provider_type"
        :skills="result.skills"
        :show-specialty="showSpecialty"
        @add-filters="handleAddFilters"
      />

      <div class="sp-finder-result__details">
        <FinderResultAddress
          layout="bio"
          :result="result"
          :result-type="resultType"
          :result-config="resultConfig"
          :labels="labels"
        >
          <FinderResultLocationActions
            layout="bio"
            :result="result"
            :result-config="resultConfig"
            @show-map="handleShowMap"
          />
        </FinderResultAddress>

        <FinderResultAttributes
          layout="bio"
          :result="result"
          :result-type="resultType"
          :result-config="resultConfig"
          :labels="labels"
        />

        <slot name="bio-details" :hours-badge-availability="hoursBadgeAvailability"></slot>
      </div>

      <FinderResultScheduling
        class="sp-finder-result__scheduling"
        :result="result"
        :scheduling="scheduling"
        @show-more-slots="handleShowMoreSlots"
        @show-date="handleShowDate"
      />
    </article>
  </div>
  <article v-else-if="showDirectory" ref="root" class="sp-finder-result" :class="classModifiers" part="base">
    <FinderResultThumbnail class="sp-finder-result__thumbnail" v-bind="thumbnailProps" />

    <div class="sp-finder-result__info">
      <FinderResultHeading
        class="sp-finder-result__heading"
        :name="displayName"
        :url="result.url"
        :provider-type="result.provider_type"
        :skills="result.skills"
        :show-specialty="showSpecialty"
        @add-filters="handleAddFilters"
      />

      <div class="sp-finder-result__body">
        <div v-if="hasAddressRegion" class="sp-finder-result__address-region">
          <FinderResultAddress
            :result="result"
            :result-type="resultType"
            :result-config="resultConfig"
            :labels="labels"
          />

          <FinderResultHoursBadge v-if="showHoursBadge" :availability="hoursBadgeAvailability" />
        </div>

        <FinderResultLocationActions
          v-if="hasAddress"
          :result="result"
          :result-config="resultConfig"
          @show-map="handleShowMap"
        />

        <FinderResultAttributes
          :result="result"
          :result-type="resultType"
          :result-config="resultConfig"
          :is-directory-layout="isDirectoryLayout"
          :labels="labels"
        />
      </div>
    </div>

    <FinderResultDirectoryDetails
      v-if="isDirectoryLayout"
      class="sp-finder-result__details"
      :layout="resultLayout"
      v-bind="directoryDetailsProps"
    />
  </article>
</template>

<script setup>
import { computed, onMounted, ref, useTemplateRef } from "vue";
import { useAddressAvailabilityFetch } from "#ce/composables/useAddressAvailabilityFetch";
import { useContainerBreakpoints } from "#ce/composables/useContainerBreakpoints";
import { whenI18nReady } from "#ce/components/SpFinder/i18n";
import { toBoolean } from "#ce/utils/props";
import { t, whenComponentsI18nReady } from "#ce/shared/components-i18n";
import FinderResultAddress from "./FinderResultAddress.ce.vue";
import FinderResultAttributes from "./FinderResultAttributes.ce.vue";
import FinderResultDirectoryDetails from "./FinderResultDirectoryDetails.ce.vue";
import FinderResultHeading from "./FinderResultHeading.ce.vue";
import FinderResultHoursBadge from "./FinderResultHoursBadge.ce.vue";
import FinderResultLocationActions from "./FinderResultLocationActions.ce.vue";
import FinderResultRatings from "./FinderResultRatings.ce.vue";
import FinderResultScheduling from "./FinderResultScheduling.ce.vue";
import FinderResultThumbnail from "./FinderResultThumbnail.ce.vue";
import { props as propsDefinitions } from "#ce/components/SpFinder/props/finder-result.js";
import { props as providerPropsDefinitions } from "#ce/components/SpFinder/props/provider-result.js";

const emit = defineEmits(["add-filters", "show-map", "show-more-slots", "show-date"]);

// First paint waits for the finder and component catalogs, which the labels and the unrated rating read.
const ready = ref(false);
onMounted(async () => {
  await Promise.all([whenI18nReady, whenComponentsI18nReady]);
  ready.value = true;
});

const props = defineProps({
  ...propsDefinitions,
  ...providerPropsDefinitions,
  /**
   * The result kind the parts draw: "provider" or "location".
   *
   * @type {String}
   */
  resultType: {
    type: String,
    required: true,
  },
  /**
   * The thumbnail's result-specific props: image, group images, count, badge and awards.
   *
   * @type {Object}
   */
  thumbnail: {
    type: Object,
    default: () => ({}),
  },
});

const root = useTemplateRef("root");
const { isMedium, isLarge, isXLarge } = useContainerBreakpoints(root, {
  small: { max: 559 },
  medium: { min: 560, max: 879 },
  large: { min: 880, max: 1079 },
  xlarge: { min: 1080 },
});

const displayName = computed(() => props.result.label || props.result.name || "");

// Config flags
const isDirectoryLayout = computed(() => props.resultConfig?.resultTemplate === "directory");
const showSpecialty = computed(() => Boolean(props.resultConfig?.showProviderSpecialty));
// Gates the location actions (Show on Map / distance / more-locations) — they render only
// when address display is on, preserving the legacy behaviour where they lived inside the
// `<address v-if="hasAddress">` block.
const hasAddress = computed(() => Boolean(props.resultConfig?.showProviderAddress));
const fetchHoursBadge = computed(() =>
  Boolean(props.resultConfig?.showProviderHoursBadge && !props.result.availability && props.result.hours_badge_url),
);
const { availability: fetchedAvailability } = useAddressAvailabilityFetch({
  url: computed(() => props.result.hours_badge_url),
  enabled: fetchHoursBadge,
});
const hoursBadgeAvailability = computed(() => {
  if (!props.resultConfig?.showProviderHoursBadge) {
    return undefined;
  }
  return props.result.availability ?? fetchedAvailability.value;
});
const showHoursBadge = computed(() => Boolean(hoursBadgeAvailability.value));
// Wraps the address and its hours badge into one region so they sit closer together
// (via --address-region-gap) than the other body regions. Rendered when either is shown.
const hasAddressRegion = computed(() => hasAddress.value || showHoursBadge.value);

// Layout
const resultLayout = computed(() => {
  if (isXLarge.value) {
    return "xl";
  }

  if (isLarge.value) {
    return "lg";
  }

  if (isMedium.value) {
    return "md";
  }

  return "sm";
});

// Child props
const thumbnailProps = computed(() => ({
  resultType: props.resultType,
  name: displayName.value,
  url: props.result.url,
  ...props.thumbnail,
}));

const directoryDetailsProps = computed(() => {
  const result = props.result;
  const config = props.resultConfig;

  return {
    // Normalized "provider" | "location" — selects the details panel's section set.
    resultType: props.resultType,
    phoneNumber: config?.showProviderPhoneNumber ? result.phone_number : "",
    faxNumber: config?.showProviderFaxNumber ? result.fax_number : "",
    gender: result.gender,
    languages: result.languages || [],
    certifications: result.certifications || [],
    educations: result.educations || [],
    plansAcceptedCount: result.plans_accepted_count,
    insurancePlansUrl: result.insurance_plans_url,
    // The disclaimer label is resolved per result type upstream (blank unless a
    // publishing domain configures one); the details panel renders it for locations only.
    disclaimer: props.labels.disclaimer,
    disclaimerHeading: props.labels.disclaimerHeading,
    // Location web contact and services are not serialized yet, so the panel's
    // website/email/services props are left at their defaults (sections stay hidden).
    // Wire them here once a search serializer provides the fields.
  };
});

const classModifiers = computed(() => `--layout-${resultLayout.value}`);

const isBio = computed(() => props.layout === "bio");
const showBio = computed(() => ready.value && isBio.value);
const showDirectory = computed(() => ready.value && !isBio.value);
const showRatingCount = computed(() => toBoolean(props.showratingcount));
// An unrated result shows only the deployment's not-rated wording, so an empty wording leaves nothing to show.
const hideRating = computed(
  () => !props.result.rating?.rated && (toBoolean(props.hideunratedrating) || t("rating_not_rated") === ""),
);
const showRatings = computed(
  () => Boolean(props.resultConfig?.showProviderRatings && props.result.rating) && !hideRating.value,
);
const bioThumbnailProps = computed(() => ({ ...thumbnailProps.value, badge: null, independentBadgeText: "" }));
const bioModifiers = computed(() => ({
  "--slot-cards": props.scheduling.slotsUi === "cards" && Boolean(props.result.appointment_slots),
  "--stack-until-wide": toBoolean(props.stackuntilwide),
}));

function handleShowMoreSlots(payload) {
  emit("show-more-slots", payload);
}

function handleShowDate(date) {
  emit("show-date", date);
}

function handleShowMap(payload) {
  emit("show-map", payload);
}

function handleAddFilters(payload) {
  emit("add-filters", payload);
}
</script>

<style>
:host {
  /* Layout */
  --column-gap: var(--sp-comp-finder-result-column-gap, var(--sp-ref-scale-24));
  --row-gap: var(--sp-comp-finder-result-row-gap, var(--sp-ref-scale-32));
  --info-gap: var(--sp-comp-finder-result-info-gap, var(--row-gap));
  --body-gap: var(--sp-comp-finder-result-body-gap, var(--sp-ref-scale-24));
  --address-region-gap: var(--sp-comp-finder-result-address-region-gap, var(--sp-ref-scale-12));
  --content-min-width: var(--sp-comp-finder-result-content-min-width, 16rem);

  /* Heading */
  --heading-text-align-sm: var(--sp-comp-finder-result-heading-text-align-sm, center);

  /* Thumbnail */
  --thumbnail-width: var(--sp-comp-finder-result-thumbnail-width, 12rem);
  --thumbnail-min-width: var(--sp-comp-finder-result-thumbnail-min-width, 8rem);
  --thumbnail-justify-self-sm: var(--sp-comp-finder-result-thumbnail-justify-self-sm, center);
  --thumbnail-padding-block-sm: var(--sp-comp-finder-result-thumbnail-padding-block-sm, var(--sp-ref-scale-8));

  /* Details */
  --details-width-lg: var(--sp-comp-finder-result-details-width-lg, 24rem);
  --details-width-xl: var(--sp-comp-finder-result-details-width-xl, 38rem);

  /* Color */
  --background: var(--sp-comp-finder-result-background, transparent);
  --color: var(--sp-comp-finder-result-color, var(--sp-sys-color-on-surface));

  /* Typography */
  --font-family: var(--sp-comp-finder-result-font-family, var(--sp-sys-font-family-normal));

  display: block;
  width: 100%;
}
</style>

<style lang="scss" scoped>
@use "@assets/mixins/breakpoint" as *;

// The layout breakpoints query the nearest `finder-result` container, which a list can take over by renaming this one.
.sp-finder-result-container {
  container-type: inline-size;
  container-name: var(--sp-comp-finder-result-container-name, finder-result);
}

.sp-finder-result.--layout-bio {
  --bio-padding-block: var(--sp-comp-finder-result-padding-block, 0);
  --bio-padding-inline: var(--sp-comp-finder-result-padding-inline, 0);
  --bio-column-gap: var(--sp-comp-finder-result-column-gap-xs, 15px);
  --bio-row-gap: var(--sp-comp-finder-result-row-gap-xs, 0);
  --bio-columns: minmax(0, 1fr);
  --bio-areas: var(--sp-comp-finder-result-grid-template-areas-xs, "heading" "thumbnail" "details" "scheduling");
  --bio-title-font: var(--sp-comp-finder-result-title-font-xs, var(--sp-sys-typescale-h5-short));

  display: grid;
  grid-template-columns: var(--bio-columns);
  grid-template-areas: var(--bio-areas);
  grid-template-rows: var(--sp-comp-finder-result-grid-template-rows, auto);
  grid-auto-rows: min-content;
  gap: var(--bio-row-gap) var(--bio-column-gap);
  box-sizing: border-box;
  width: 100%;
  padding: var(--bio-padding-block) var(--bio-padding-inline);
  background: var(--background);
  color: var(--color);

  @include sp-breakpoint(sm) {
    --bio-title-font: var(--sp-comp-finder-result-title-font-sm, var(--sp-sys-typescale-h4-short));
  }

  @include sp-breakpoint(md) {
    --bio-title-font: var(--sp-comp-finder-result-title-font, var(--sp-sys-typescale-h3-short));
  }

  &.--slot-cards {
    --bio-row-gap: var(--sp-comp-finder-result-slot-cards-row-gap, var(--sp-ref-scale-24));
  }

  @mixin bio-columns {
    --bio-column-gap: var(--sp-comp-finder-result-column-gap-sm, 20px);
    --bio-row-gap: var(--sp-comp-finder-result-row-gap-sm, 15px);
    --bio-columns: 100px 200px 1fr;
    --bio-areas: "thumbnail heading scheduling" "thumbnail details scheduling";
  }

  &:where(:not(.--stack-until-wide)) {
    @container finder-result (width > 500px) {
      @include bio-columns;
    }
  }

  &:where(.--stack-until-wide) {
    @container finder-result (width > 750px) {
      @include bio-columns;
    }
  }

  @container finder-result (width > 970px) {
    --bio-columns: minmax(120px, 180px) 1fr 55%;
  }

  @container finder-result (width > 1200px) {
    --bio-columns: minmax(120px, 180px) 1fr 650px;

    &.--slot-cards {
      --bio-columns: minmax(120px, 180px) minmax(27ch, 1fr) 820px;
    }
  }

  .sp-finder-result__thumbnail {
    grid-area: thumbnail;
    width: auto;
    padding: 0;

    @include sp-breakpoint(xs) {
      margin-block-end: var(--sp-comp-finder-result-thumbnail-margin-block-end-xs, 0);
    }
  }

  .sp-finder-result__heading {
    --sp-comp-text-custom-font-short: var(--bio-title-font);
    --heading-title-font: var(--bio-title-font);
    --heading-title-margin: 0 0 0.25em;
    --heading-specialties-margin: 0;

    grid-area: heading;
    padding: var(--sp-comp-finder-result-heading-padding, 0);
    text-align: var(--sp-comp-finder-result-heading-text-align);

    @include sp-breakpoint(xs) {
      margin-block-end: var(--sp-comp-finder-result-heading-margin-block-end-xs, 0);
      text-align: var(
        --sp-comp-finder-result-heading-text-align-xs,
        var(--sp-comp-finder-result-heading-text-align, left)
      );
    }
  }

  .sp-finder-result__details {
    grid-area: details;
    position: relative;
    padding: var(--sp-comp-finder-result-details-padding, 0);
    line-height: 1.5em;
  }

  .sp-finder-result__scheduling {
    grid-area: scheduling;
    overflow: hidden;
    padding: var(--sp-comp-finder-result-scheduling-padding, 0);
    border-top: var(--sp-comp-finder-result-scheduling-border-top, none);

    @include sp-breakpoint(xs) {
      padding-top: var(--sp-comp-finder-result-scheduling-padding-top-xs, 40px);
    }
  }

  &.--slot-cards .sp-finder-result__scheduling {
    overflow: visible;
    padding-top: 0;
  }
}

.sp-finder-result {
  --grid-columns: minmax(0, 1fr);
  --grid-template-areas: "heading" "thumbnail" "body" "details";
  --info-display: contents;

  display: grid;
  width: 100%;
  box-sizing: border-box;
  grid-template-columns: var(--grid-columns);
  grid-template-areas: var(--grid-template-areas);
  gap: var(--row-gap) var(--column-gap);
  align-items: start;
  background: var(--background);
  color: var(--color);
  font-family: var(--font-family);

  &.--layout-sm {
    --heading-text-align: var(--heading-text-align-sm);
    --thumbnail-justify-self: var(--thumbnail-justify-self-sm);
    --thumbnail-padding-block: var(--thumbnail-padding-block-sm);
  }

  &.--layout-md {
    --info-display: flex;
    --grid-columns: minmax(var(--thumbnail-min-width), var(--thumbnail-width)) minmax(0, 1fr);
    --grid-template-areas: "thumbnail info" "details details";
  }

  &.--layout-lg,
  &.--layout-xl {
    --info-display: flex;
    --details-width: var(--details-width-lg);
    --grid-columns: minmax(var(--thumbnail-min-width), var(--thumbnail-width)) minmax(var(--content-min-width), 1fr)
      minmax(0, var(--details-width));
    --grid-template-areas: "thumbnail info details";
  }

  &.--layout-xl {
    --details-width: var(--details-width-xl);
  }

  &__thumbnail {
    grid-area: thumbnail;
    width: var(--thumbnail-width);
    max-width: 100%;
    justify-self: var(--thumbnail-justify-self, auto);
    padding-block: var(--thumbnail-padding-block, 0);
  }

  &__info {
    display: var(--info-display);
    min-width: 0;
    flex-direction: column;
    grid-area: info;
    gap: var(--info-gap);
  }

  &__heading {
    grid-area: heading;
    text-align: var(--heading-text-align, inherit);
  }

  &__body {
    display: flex;
    min-width: 0;
    flex-direction: column;
    grid-area: body;
    gap: var(--body-gap);
  }

  &__address-region {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: var(--address-region-gap);
  }

  &__details {
    grid-area: details;
    min-width: 0;
  }
}
</style>
